import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiGet, apiPath, apiPost, apiPut } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import { ApiErrorBanner } from './ApiErrorBanner';
import { StatusBadge } from './StatusBadge';
import type { FlowModel, FlowModelRequest, FlowModelStep } from './types';

const SUM_EPSILON = 0.0001;

interface DraftStep extends FlowModelStep {
  rowKey: string;
}

/**
 * Per-flow-model editor. Used for both /parameters/flow-models/new and
 * /parameters/flow-models/:id. Live SUM=100% validator + strict-increasing
 * nb_days check + add/remove rows.
 *
 * Editing an ACTIVE model demotes it to DRAFT (server-side); the UI shows
 * a banner so the admin knows re-approval is required after save.
 */
export function FlowModelEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Load existing (edit mode) ────────────────────────────────────────────
  const existing = useQuery<FlowModel, CubixApiError>({
    queryKey: ['parameters', 'flow-model', id],
    queryFn: () => apiGet<FlowModel>(apiPath('alm', 'flow-models', id!)),
    enabled: !isNew,
  });

  // ── Editor state ─────────────────────────────────────────────────────────
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<DraftStep[]>([emptyStep(1)]);

  // Hydrate when the existing model loads.
  useEffect(() => {
    if (!existing.data) return;
    setCode(existing.data.code);
    setName(existing.data.name);
    setDescription(existing.data.description ?? '');
    setSteps(
      existing.data.steps.map((s) => ({ ...s, rowKey: cryptoKey() })),
    );
  }, [existing.data]);

  // ── Live validation ──────────────────────────────────────────────────────
  const sum = useMemo(
    () => steps.reduce((a, s) => a + (Number(s.valuePct) || 0), 0),
    [steps],
  );
  const sumOk   = Math.abs(sum - 1) < SUM_EPSILON;
  const sortedDays = [...steps].map((s) => s.nbDays);
  const daysIncreasing = sortedDays.every((d, i) => i === 0 || d > sortedDays[i - 1]);
  const noDuplicates   = new Set(sortedDays).size === sortedDays.length;
  const valid =
    code.trim() !== '' &&
    name.trim() !== '' &&
    steps.length > 0 &&
    sumOk &&
    daysIncreasing &&
    noDuplicates &&
    steps.every(
      (s) =>
        s.periodLabel.trim() !== '' &&
        s.nbDays > 0 &&
        Number(s.valuePct) >= 0,
    );

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = useMutation<FlowModel, CubixApiError, FlowModelRequest>({
    mutationFn: (body) =>
      isNew
        ? apiPost<FlowModel, FlowModelRequest>(apiPath('alm', 'flow-models'), body)
        : apiPut<FlowModel, FlowModelRequest>(apiPath('alm', 'flow-models', id!), body),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['parameters', 'flow-models'] });
      qc.invalidateQueries({ queryKey: ['parameters', 'flow-model', saved.id] });
      // After creating a brand-new model, return to the list — the user
      // typically wants to see it land in the table (status DRAFT, awaiting
      // approval). Edits to an existing model stay on the editor.
      if (isNew) navigate('/parameters/flow-models');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    save.mutate({
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      steps: steps
        .map(({ rowKey, ...rest }) => rest)
        .sort((a, b) => a.stepIndex - b.stepIndex),
    });
  }

  function addRow() {
    const lastDays = steps.length ? steps[steps.length - 1].nbDays : 0;
    const nextIdx  = (steps[steps.length - 1]?.stepIndex ?? 0) + 1;
    setSteps([
      ...steps,
      { rowKey: cryptoKey(), stepIndex: nextIdx, periodLabel: '', nbDays: lastDays + 30, valuePct: 0 },
    ]);
  }

  function removeRow(rowKey: string) {
    if (steps.length <= 1) return;
    const filtered = steps.filter((s) => s.rowKey !== rowKey);
    // Renumber stepIndex 1..n so it stays contiguous.
    setSteps(filtered.map((s, i) => ({ ...s, stepIndex: i + 1 })));
  }

  function patchRow(rowKey: string, patch: Partial<DraftStep>) {
    setSteps(steps.map((s) => (s.rowKey === rowKey ? { ...s, ...patch } : s)));
  }

  // ── Render ───────────────────────────────────────────────────────────────
  if (!isNew && existing.isLoading) {
    return <p className="p-8 text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</p>;
  }
  if (!isNew && existing.isError) {
    return <ApiErrorBanner error={existing.error} />;
  }

  const wasActive = existing.data?.status === 'ACTIVE';

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2">
            <Link to="/parameters/flow-models">
              <ArrowLeft className="mr-1 h-4 w-4" /> All flow models
            </Link>
          </Button>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            {isNew ? 'New flow model' : `Edit flow model · ${existing.data?.code}`}
            {existing.data ? <StatusBadge status={existing.data.status} /> : null}
          </h1>
          <p className="text-sm text-muted-foreground">
            Each row is a maturity step. Sum of <code>value_pct</code> must equal 100%.
            Days must strictly increase.
          </p>
        </div>
      </header>

      {!isNew && wasActive ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            This model is <strong>ACTIVE</strong>. Saving any change will demote it to{' '}
            <strong>DRAFT</strong> — an admin will need to re-approve before it can be
            referenced by affectations.
          </p>
        </div>
      ) : null}

      {isNew ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            New flow models are saved as <strong>DRAFT</strong>. They won't appear in the
            Affectations dropdown until an admin approves them on the Flow Models page.
          </p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Header</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="fm-code">Code</Label>
                <Input
                  id="fm-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="FM_DEP_LIQ"
                  required
                  disabled={!isNew}
                />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label htmlFor="fm-name">Name</Label>
                <Input
                  id="fm-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label htmlFor="fm-desc">Description</Label>
                <Input
                  id="fm-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Steps</CardTitle>
            <CardDescription>
              Click + to add a row. Each row's value % is a fraction of notional
              (0.0258 = 2.58%). Final row should leave the total at exactly 100%.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">Period label</th>
                    <th className="px-3 py-2 text-right font-medium">Days from value</th>
                    <th className="px-3 py-2 text-right font-medium">Value (fraction)</th>
                    <th className="px-3 py-2 text-right font-medium">% of notional</th>
                    <th className="px-3 py-2 text-left font-medium">Notes</th>
                    <th className="px-3 py-2 text-right font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {steps.map((s, i) => {
                    const prev = i > 0 ? steps[i - 1] : null;
                    const daysBad = prev !== null && s.nbDays <= prev.nbDays;
                    const dupBad =
                      steps.filter((x) => x.nbDays === s.nbDays).length > 1;
                    return (
                      <tr key={s.rowKey} className="border-b last:border-0 align-top">
                        <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                          {s.stepIndex}
                        </td>
                        <td className="px-3 py-1">
                          <Input
                            value={s.periodLabel}
                            onChange={(e) =>
                              patchRow(s.rowKey, { periodLabel: e.target.value })
                            }
                            placeholder="6m"
                            className="h-8"
                          />
                        </td>
                        <td className="px-3 py-1">
                          <Input
                            type="number"
                            min={1}
                            value={s.nbDays}
                            onChange={(e) =>
                              patchRow(s.rowKey, {
                                nbDays: Number(e.target.value) || 0,
                              })
                            }
                            className={cn(
                              'h-8 text-right tabular-nums',
                              (daysBad || dupBad) && 'border-destructive',
                            )}
                          />
                        </td>
                        <td className="px-3 py-1">
                          <Input
                            type="number"
                            step="0.000001"
                            min={0}
                            value={s.valuePct}
                            onChange={(e) =>
                              patchRow(s.rowKey, {
                                valuePct: Number(e.target.value) || 0,
                              })
                            }
                            className="h-8 text-right tabular-nums"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs tabular-nums text-muted-foreground">
                          {(Number(s.valuePct || 0) * 100).toFixed(4)}%
                        </td>
                        <td className="px-3 py-1">
                          <Input
                            value={s.notes ?? ''}
                            onChange={(e) =>
                              patchRow(s.rowKey, { notes: e.target.value })
                            }
                            className="h-8"
                          />
                        </td>
                        <td className="px-3 py-1 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRow(s.rowKey)}
                            disabled={steps.length <= 1}
                            title="Remove row"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="border-t bg-muted/30">
                  <tr>
                    <td colSpan={3} className="px-3 py-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addRow}
                      >
                        <Plus className="mr-1 h-4 w-4" /> Add row
                      </Button>
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                      Σ value:
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums font-medium',
                        sumOk ? 'text-emerald-700' : 'text-destructive',
                      )}
                    >
                      {(sum * 100).toFixed(4)}%
                    </td>
                    <td colSpan={2} className="px-3 py-2 text-right">
                      {sumOk ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" /> exactly 100%
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-destructive">
                          <AlertTriangle className="h-3.5 w-3.5" /> must equal 100%
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        <ValidationStatus
          sumOk={sumOk}
          daysIncreasing={daysIncreasing}
          noDuplicates={noDuplicates}
        />

        {save.isError ? <ApiErrorBanner error={save.error} /> : null}
        {save.isSuccess ? (
          <p className="flex items-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" /> Saved.
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={!valid || save.isPending}>
            {save.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isNew ? 'Create flow model' : 'Save changes'}
          </Button>
          <Button type="button" variant="ghost" asChild>
            <Link to="/parameters/flow-models">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function ValidationStatus({
  sumOk,
  daysIncreasing,
  noDuplicates,
}: {
  sumOk: boolean;
  daysIncreasing: boolean;
  noDuplicates: boolean;
}) {
  const items: Array<{ ok: boolean; label: string }> = [
    { ok: sumOk, label: 'Σ value_pct equals 1.0000 (100%)' },
    { ok: daysIncreasing, label: 'nb_days strictly increases' },
    { ok: noDuplicates, label: 'no duplicate nb_days' },
  ];
  return (
    <ul className="grid gap-1 text-xs md:grid-cols-3">
      {items.map((it, i) => (
        <li
          key={i}
          className={cn(
            'flex items-center gap-1.5 rounded-md border px-2 py-1',
            it.ok
              ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-700'
              : 'border-destructive/40 bg-destructive/5 text-destructive',
          )}
        >
          {it.ok ? (
            <CheckCircle2 className="h-3.5 w-3.5" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5" />
          )}
          {it.label}
        </li>
      ))}
    </ul>
  );
}

function emptyStep(stepIndex: number): DraftStep {
  return {
    rowKey: cryptoKey(),
    stepIndex,
    periodLabel: '',
    nbDays: 30 * stepIndex,
    valuePct: 0,
    notes: '',
  };
}

function cryptoKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}
